import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Droplet } from "lucide-react";

const FAQ = [
  { q: "How long does an IV take?", a: "Most infusions run 30–60 minutes, plus a brief check-in with your provider." },
  { q: "Do I need to be a member?", a: "No — memberships offer perks and pricing, but anyone can book an IV." },
  { q: "Will I feel it right away?", a: "Many clients feel refreshed within an hour; effects vary by blend and individual." },
];

export default function IVTherapy() {
  const [types, setTypes] = useState([]);
  useEffect(() => { api.get("/iv-types").then(({data}) => setTypes(data)).catch(()=>{}); }, []);

  return (
    <div data-testid="iv-therapy-page">
      <section className="section-y">
        <div className="container-x grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <div className="overline">IV Therapy</div>
            <h1 className="font-display text-5xl lg:text-6xl text-sage-800 mt-4 text-balance">Hydrate. Restore. Feel it.</h1>
            <p className="mt-6 text-lg text-sage-800/70 leading-relaxed max-w-lg">
              Direct-to-bloodstream hydration and nutrient support, formulated around your wellness goals. Choose from focused blends or ask your provider to personalize one.
            </p>
            <div className="mt-8 flex gap-3 flex-wrap">
              <Button asChild className="btn-pill h-11 px-6 bg-sage-700 hover:bg-sage-800 text-white" data-testid="iv-book-consult">
                <Link to="/book/consult">Book a Consult First</Link>
              </Button>
              <Button asChild variant="outline" className="btn-pill h-11 px-6 border-sage-700/30 text-sage-800" data-testid="iv-join-member">
                <Link to="/memberships">Join & Book IV</Link>
              </Button>
            </div>
          </div>
          <div className="rounded-3xl overflow-hidden aspect-[4/5]">
            <img src="https://restorergv.com/wp-content/uploads/2024/09/iv-therapy-2-1024x819.jpg" alt="IV Therapy" className="w-full h-full object-cover" />
          </div>
        </div>
      </section>

      <section className="pb-24">
        <div className="container-x">
          <div className="mb-10">
            <div className="overline">Signature blends</div>
            <h2 className="font-display text-3xl lg:text-4xl text-sage-800 mt-3">Formulated for how you want to feel.</h2>
          </div>
          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {types.map((t) => (
              <div key={t.id} data-testid={`iv-blend-${t.id}`} className="card-lift bg-white border border-sand-200/60 rounded-3xl p-7">
                <Droplet className="w-7 h-7 text-clay-500" strokeWidth={1.4} />
                <div className="font-display text-2xl text-sage-800 mt-6">{t.name}</div>
                <p className="mt-2 text-sage-800/65 text-sm leading-relaxed">{t.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="pb-24">
        <div className="container-x max-w-3xl">
          <h2 className="font-display text-3xl lg:text-4xl text-sage-800">Common questions</h2>
          <Accordion type="single" collapsible className="mt-6">
            {FAQ.map((f, i) => (
              <AccordionItem key={i} value={`i${i}`} className="border-sand-200">
                <AccordionTrigger data-testid={`iv-faq-${i}`} className="text-left text-sage-800 hover:no-underline">{f.q}</AccordionTrigger>
                <AccordionContent className="text-sage-800/70">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    </div>
  );
}
